import jsPDF from 'jspdf';
import { VisitaComprovante } from '../types';
import { ConfiguracoesService } from './configuracoesService';

// @ts-ignore - qrcode types
const QRCode = require('qrcode');

interface ComprovanteData {
  codigo: string;
  id: string;
  cliente: string;
  data_desejada: string;
  produto: string;
  quantidade: number;
  volume: string;
  tipo_carga: string;
  status: string;
  observacoes: string;
  criado_em: string;
  criado_por: string;
  filial: string;
  ordem_chegada?: number | null;
  hora_ordem_chegada?: string | null;
}

// Função auxiliar para carregar imagens
const loadImage = async (path: string): Promise<string> => {
  try {
    const response = await fetch(path);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    throw error;
  }
};

// Função auxiliar para formatar data UTC sem aplicar fuso horário local
const formatarDataUTC = (dataString: string): string => {
  const data = new Date(dataString);
  const ano = data.getUTCFullYear();
  const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
  const dia = String(data.getUTCDate()).padStart(2, '0');
  return `${dia}/${mes}/${ano}`;
};

export const ComprovanteService = {
  async gerarComprovanteAgendamento(solicitacao: ComprovanteData): Promise<boolean> {
    // Buscar termos e regras dinamicamente do backend
    let termosRegrasTexto = '';
    let horarioFuncionamento = '08:00 às 18:00';
    let horarioRecebimentoNF = '08:00 às 17:00';
    try {
      const configuracoes = await ConfiguracoesService.buscarTodas(solicitacao.filial);
      const termosTexto = configuracoes.find(c => c.chave === 'termos_regras_texto');
      const horarioAtendimento = configuracoes.find(c => c.chave === 'horario_atendimento');
      const horarioRecebimentoNFConfig = configuracoes.find(c => c.chave === 'horario_recebimento_nf');
      
      if (termosTexto) {
        termosRegrasTexto = termosTexto.valor;
      }
      
      // Extrair horário de funcionamento (formato: "Segunda a Sexta: 08:00 às 18:00")
      if (horarioAtendimento?.valor) {
        const match = horarioAtendimento.valor.match(/(\d{2}:\d{2})\s*às\s*(\d{2}:\d{2})/);
        if (match) {
          horarioFuncionamento = `${match[1]} às ${match[2]}`;
        }
      }
      
      // Extrair horário de recebimento de NF (formato: "Segunda a Sexta: 08:00 às 17:00")
      if (horarioRecebimentoNFConfig?.valor) {
        const match = horarioRecebimentoNFConfig.valor.match(/(\d{2}:\d{2})\s*às\s*(\d{2}:\d{2})/);
        if (match) {
          horarioRecebimentoNF = `${match[1]} às ${match[2]}`;
        }
      }
    } catch (error) {
      console.error('Erro ao buscar termos e regras:', error);
    }
    try {
      const doc = new jsPDF();

      // Configurações de cor - PALETA FALCON HUB
      const corAzulEscuro: [number, number, number] = [15, 23, 42]; // Azul escuro principal
      const corAzul: [number, number, number] = [59, 130, 246]; // Azul secundário
      const corVerde: [number, number, number] = [34, 197, 94]; // Verde accent
      const corPreto: [number, number, number] = [0, 0, 0];
      const corBranco: [number, number, number] = [255, 255, 255];
      const corCinza: [number, number, number] = [148, 163, 184];

      // Mapeamento de logos de filiais
      const logoFilialMap: Record<string, string> = {
        'FILIAL_1': '/Filial 1- UP DISTRIBUIDORA.png',
        'FILIAL_2': '/Filial 2- Aleplast.png',
        'FILIAL_3': '/Filial 3- Alepack.png'
      };
      const logoFilialPath = logoFilialMap[solicitacao.filial] || '';

      // Header com gradiente simulado (azul escuro para azul)
      doc.setFillColor(...corAzulEscuro);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setFillColor(...corAzul);
      doc.rect(0, 35, 210, 5, 'F');

      // Logo Falcon Hub 2
      try {
        const falconHubLogo = await loadImage('/Falcon Hub 2.png');
        doc.addImage(falconHubLogo, 'PNG', 15, 8, 30, 24);
      } catch (logoError) {
        console.error('Erro ao carregar logo Falcon Hub:', logoError);
        // Fallback texto se a logo não carregar
        doc.setFontSize(20);
        doc.setTextColor(...corBranco);
        doc.setFont('helvetica', 'bold');
        doc.text('FALCON HUB', 15, 22);
      }

      // Título principal
      doc.setFontSize(18);
      doc.setTextColor(...corBranco);
      doc.setFont('helvetica', 'bold');
      doc.text('COMPROVANTE DE AGENDAMENTO', 105, 25, { align: 'center' });

      // Logo da filial no canto direito
      if (logoFilialPath) {
        try {
          const filialLogo = await loadImage(logoFilialPath);
          doc.addImage(filialLogo, 'PNG', 165, 8, 30, 24);
        } catch (logoError) {
          console.error('Erro ao carregar logo filial:', logoError);
          // Fallback texto se a logo não carregar
          doc.setFontSize(12);
          doc.setTextColor(...corBranco);
          doc.setFont('helvetica', 'bold');
          doc.text(solicitacao.filial.replace('_', ' '), 180, 22, { align: 'center' });
        }
      }

      // Linha decorativa verde
      doc.setDrawColor(...corVerde);
      doc.setLineWidth(3);
      doc.line(0, 40, 210, 40);

      let yPos = 50;

      // INFORMAÇÕES PRINCIPAIS EM DESTAQUE VERDE
      doc.setFillColor(...corVerde);
      doc.roundedRect(15, yPos, 180, 18, 2, 2, 'F');

      doc.setFontSize(10);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'bold');
      doc.text('CÓDIGO', 20, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.codigo), 50, yPos + 7);

      doc.setFont('helvetica', 'bold');
      doc.text('STATUS', 100, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.status).toUpperCase(), 120, yPos + 7);

      doc.setFont('helvetica', 'bold');
      doc.text('DATA:', 20, yPos + 14);
      doc.setFont('helvetica', 'normal');
      const dataDesejada = formatarDataUTC(solicitacao.data_desejada);
      doc.text(dataDesejada, 35, yPos + 14);

      yPos += 25;

      // INFORMAÇÕES DO FORNECEDOR - layout em duas colunas
      doc.setFontSize(11);
      doc.setTextColor(...corAzul);
      doc.setFont('helvetica', 'bold');
      doc.text('DADOS DA SOLICITAÇÃO', 15, yPos);
      yPos += 6;

      doc.setDrawColor(...corAzulEscuro);
      doc.setLineWidth(0.5);
      doc.line(15, yPos, 195, yPos);
      yPos += 5;

      doc.setFontSize(9);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'normal');

      // Coluna esquerda
      const col1X = 15;
      const col2X = 105;
      let col1Y = yPos;
      let col2Y = yPos;

      doc.setFont('helvetica', 'bold');
      doc.text('Fornecedor:', col1X, col1Y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.cliente), col1X + 30, col1Y);
      col1Y += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('Produto:', col1X, col1Y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.produto), col1X + 30, col1Y);
      col1Y += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('Peso:', col1X, col1Y);
      doc.setFont('helvetica', 'normal');
      doc.text(`${solicitacao.quantidade.toLocaleString('pt-BR')} kg`, col1X + 30, col1Y);
      col1Y += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('Volume:', col1X, col1Y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.volume || 'Não informado'), col1X + 30, col1Y);
      col1Y += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('Tipo Carga:', col1X, col1Y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.tipo_carga || 'Não informado'), col1X + 30, col1Y);
      col1Y += 5;

      // Coluna direita
      doc.setFont('helvetica', 'bold');
      doc.text('Filial:', col2X, col2Y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.filial || 'Não informada'), col2X + 25, col2Y);
      col2Y += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('Solicitante:', col2X, col2Y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(solicitacao.criado_por || 'Não informado'), col2X + 25, col2Y);
      col2Y += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('Data Solicitação:', col2X, col2Y);
      doc.setFont('helvetica', 'normal');
      doc.text(formatarDataUTC(solicitacao.criado_em), col2X + 25, col2Y);
      col2Y += 5;

      if (solicitacao.ordem_chegada) {
        doc.setFont('helvetica', 'bold');
        doc.text('Ordem Chegada:', col2X, col2Y);
        doc.setFont('helvetica', 'normal');
        doc.text(`#${solicitacao.ordem_chegada}`, col2X + 25, col2Y);
        col2Y += 5;

        if (solicitacao.hora_ordem_chegada) {
          doc.setFont('helvetica', 'bold');
          doc.text('Horário:', col2X, col2Y);
          doc.setFont('helvetica', 'normal');
          doc.text(String(solicitacao.hora_ordem_chegada), col2X + 25, col2Y);
          col2Y += 5;
        }
      }

      yPos = Math.max(col1Y, col2Y) + 5;

      // HORÁRIO DE FUNCIONAMENTO - mais compacto
      doc.setFontSize(11);
      doc.setTextColor(...corAzul);
      doc.setFont('helvetica', 'bold');
      doc.text('HORÁRIO DE FUNCIONAMENTO', 15, yPos);
      yPos += 6;

      doc.setDrawColor(...corAzulEscuro);
      doc.setLineWidth(0.5);
      doc.line(15, yPos, 195, yPos);
      yPos += 5;

      doc.setFontSize(9);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'normal');

      doc.setFont('helvetica', 'bold');
      doc.text('• Horário de Atendimento:', 15, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(horarioFuncionamento, 55, yPos);
      yPos += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('• Horário de Recebimento de NF:', 15, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(horarioRecebimentoNF, 55, yPos);
      yPos += 8;

      // TERMOS E REGRAS - mais compacto
      doc.setFontSize(11);
      doc.setTextColor(...corAzul);
      doc.setFont('helvetica', 'bold');
      doc.text('TERMOS E REGRAS', 15, yPos);
      yPos += 6;

      doc.setDrawColor(...corAzulEscuro);
      doc.setLineWidth(0.5);
      doc.line(15, yPos, 195, yPos);
      yPos += 5;

      doc.setFontSize(7);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'normal');

      // Usar termos dinâmicos se configurados, senão usar hardcoded
      const termos = termosRegrasTexto
        ? termosRegrasTexto.split('\n')
        : [
            '1) Fornecedor fora do estado - passagem obrigatória em posto fiscal',
            '   Toda carga de fornecedor localizado fora de Alagoas deve passar por um dos postos fiscais:',
            '   • Delmiro Gouveia - Rodovia AL 423, KM 101',
            '   • Novo Lino - Rodovia BR 101, KM 0',
            '   • Porto Real do Colégio - Rodovia BR 101, KM 245',
            '   • São José da Laje - Rodovia BR 104, KM 12',
            '',
            '2) Código de barras obrigatório (DUN e EAN)',
            '   Todos os produtos devem possuir código DUN e EAN, sem reutilização de códigos.',
            '',
            '3) Mudança de fator',
            '   Qualquer alteração de fator deve ser informada previamente aos setores de Compras e Logística.',
            '',
            'O descumprimento pode acarretar em reagendamento ou devolução da carga.',
          ];

      termos.forEach((linha) => {
        doc.text(linha, 15, yPos);
        yPos += 3.5;
      });

      yPos += 5;

      // OBSERVAÇÕES - se existir e se houver espaço
      if (solicitacao.observacoes && yPos < 250) {
        doc.setFontSize(11);
        doc.setTextColor(...corAzul);
        doc.setFont('helvetica', 'bold');
        doc.text('OBSERVAÇÕES', 15, yPos);
        yPos += 6;

        doc.setDrawColor(...corAzulEscuro);
        doc.setLineWidth(0.5);
        doc.line(15, yPos, 195, yPos);
        yPos += 5;

        doc.setFontSize(9);
        doc.setTextColor(...corPreto);
        doc.setFont('helvetica', 'normal');

        const maxWidth = 165;
        const lines = doc.splitTextToSize(solicitacao.observacoes, maxWidth);

        lines.forEach((line: string) => {
          if (yPos < 270) {
            doc.text(line, 15, yPos);
            yPos += 4;
          }
        });
      }

      // RODAPÉ - mais compacto
      const rodapeY = 260;
      doc.setDrawColor(...corVerde);
      doc.line(15, rodapeY, 195, rodapeY);

      doc.setFontSize(9);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'normal');
      doc.text('Comprovante oficial de agendamento - Conservar para apresentação no dia.', 105, rodapeY + 5, { align: 'center' });

      doc.setTextColor(...corAzulEscuro);
      doc.setFont('helvetica', 'bold');
      doc.text('FALCON HUB', 105, rodapeY + 10, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...corPreto);
      doc.text('Sistema de Gestão de Agendamento', 105, rodapeY + 15, { align: 'center' });
      const dataHora = `${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}`;
      doc.text(dataHora, 105, rodapeY + 20, { align: 'center' });

      // QR CODE com URL da página de visualização no rodapé
      try {
        // Substituir localhost pelo IP da máquina para funcionar no celular
        let urlVisualizacao = `${window.location.origin}/visualizar/${solicitacao.id}`;
        urlVisualizacao = urlVisualizacao.replace('localhost', window.location.hostname);
        urlVisualizacao = urlVisualizacao.replace('127.0.0.1', window.location.hostname);

        const qrCodeDataURL = await QRCode.toDataURL(urlVisualizacao, {
          width: 50,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });

        doc.addImage(qrCodeDataURL, 'PNG', 15, rodapeY + 2, 25, 25);
        
        doc.setFontSize(7);
        doc.setTextColor(...corPreto);
        doc.setFont('helvetica', 'normal');
        doc.text('Escaneie para', 27, rodapeY + 28, { align: 'center' });
        doc.text('ver detalhes', 27, rodapeY + 32, { align: 'center' });
      } catch (qrError) {
        console.error('Erro ao gerar QR code:', qrError);
      }

      // Gerar nome do arquivo e fazer download
      const nomeArquivo = `COMPROVANTE_AGENDAMENTO_${solicitacao.codigo}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nomeArquivo);

      return true;

    } catch (error) {
      console.error('Erro ao gerar comprovante:', error);
      return false;
    }
  },

  async gerarComprovanteVisita(visita: VisitaComprovante): Promise<boolean> {
    try {
      const doc = new jsPDF();

      const corAzulEscuro: [number, number, number] = [15, 23, 42];
      const corAzul: [number, number, number] = [59, 130, 246];
      const corVerde: [number, number, number] = [34, 197, 94];
      const corPreto: [number, number, number] = [0, 0, 0];
      const corBranco: [number, number, number] = [255, 255, 255];

      doc.setFillColor(...corAzulEscuro);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setFillColor(...corAzul);
      doc.rect(0, 35, 210, 5, 'F');

      try {
        const falconHubLogo = await loadImage('/Falcon Hub 2.png');
        doc.addImage(falconHubLogo, 'PNG', 15, 8, 30, 24);
      } catch (logoError) {
        console.error('Erro ao carregar logo Falcon Hub:', logoError);
        doc.setFontSize(20);
        doc.setTextColor(...corBranco);
        doc.setFont('helvetica', 'bold');
        doc.text('FALCON HUB', 15, 22);
      }

      doc.setFontSize(18);
      doc.setTextColor(...corBranco);
      doc.setFont('helvetica', 'bold');
      doc.text('COMPROVANTE DE VISITA', 105, 25, { align: 'center' });

      doc.setDrawColor(...corVerde);
      doc.setLineWidth(3);
      doc.line(0, 40, 210, 40);

      let yPos = 50;

      doc.setFillColor(...corVerde);
      doc.roundedRect(15, yPos, 180, 18, 2, 2, 'F');

      doc.setFontSize(10);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'bold');
      doc.text('CÓDIGO', 20, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.text(String(visita.codigo), 50, yPos + 7);

      doc.setFont('helvetica', 'bold');
      doc.text('STATUS', 100, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.text(String(visita.status).toUpperCase(), 120, yPos + 7);

      doc.setFont('helvetica', 'bold');
      doc.text('DATA:', 20, yPos + 14);
      doc.setFont('helvetica', 'normal');
      doc.text(String(visita.data), 35, yPos + 14);

      yPos += 25;

      doc.setFontSize(11);
      doc.setTextColor(...corAzul);
      doc.setFont('helvetica', 'bold');
      doc.text('DADOS DA VISITA', 15, yPos);
      yPos += 6;

      doc.setDrawColor(...corAzulEscuro);
      doc.setLineWidth(0.5);
      doc.line(15, yPos, 195, yPos);
      yPos += 5;

      doc.setFontSize(9);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'normal');

      const col1X = 15;
      const col2X = 105;
      let col1Y = yPos;
      let col2Y = yPos;

      const rotulos = [
        { label: 'Solicitante:', value: visita.solicitante, col: 'left' },
        { label: 'Empresa:', value: visita.empresa, col: 'left' },
        { label: 'Documento:', value: visita.documento, col: 'left' },
        { label: 'Assunto:', value: visita.assunto, col: 'left' },
        { label: 'Comprador:', value: visita.comprador, col: 'right' },
        { label: 'Horário:', value: visita.horario, col: 'right' },
        { label: 'Código de Verificação:', value: visita.codigo_verificacao, col: 'right' }
      ];

      rotulos.forEach(item => {
        const x = item.col === 'left' ? col1X : col2X;
        const y = item.col === 'left' ? col1Y : col2Y;
        doc.setFont('helvetica', 'bold');
        doc.text(item.label, x, y);
        doc.setFont('helvetica', 'normal');
        doc.text(String(item.value || 'Não informado'), x + (item.label.length > 10 ? 45 : 35), y);
        if (item.col === 'left') col1Y += 5; else col2Y += 5;
      });

      yPos = Math.max(col1Y, col2Y) + 5;

      doc.setFontSize(11);
      doc.setTextColor(...corAzul);
      doc.setFont('helvetica', 'bold');
      doc.text('INSTRUÇÕES', 15, yPos);
      yPos += 6;

      doc.setDrawColor(...corAzulEscuro);
      doc.setLineWidth(0.5);
      doc.line(15, yPos, 195, yPos);
      yPos += 5;

      doc.setFontSize(9);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'normal');

      const instrucoes = [
        '1) Apresente este comprovante oficial com documento com foto na recepção no momento do check-in.',
        '2) Chegue com antecedência. O atraso pode acarretar reagendamento ou cancelamento da visita.',
        '3) Alterações de horário devem ser solicitadas com antecedência e estão sujeitas à disponibilidade.',
        '4) O não comparecimento poderá resultar em reprovação de futuras solicitações.'
      ];

      instrucoes.forEach(linha => {
        const lines = doc.splitTextToSize(linha, 180);
        lines.forEach((line: string) => {
          doc.text(line, 15, yPos);
          yPos += 4;
        });
      });

      yPos += 5;

      const rodapeY = 260;
      doc.setDrawColor(...corVerde);
      doc.line(15, rodapeY, 195, rodapeY);

      doc.setFontSize(9);
      doc.setTextColor(...corPreto);
      doc.setFont('helvetica', 'normal');
      doc.text('Comprovante oficial de visita - Conservar para apresentação na recepção.', 105, rodapeY + 5, { align: 'center' });

      doc.setTextColor(...corAzulEscuro);
      doc.setFont('helvetica', 'bold');
      doc.text('FALCON HUB', 105, rodapeY + 10, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...corPreto);
      doc.text('Sistema de Gestão de Agendamento', 105, rodapeY + 15, { align: 'center' });
      doc.text(String(visita.gerado_em), 105, rodapeY + 20, { align: 'center' });

      try {
        const urlVisualizacao = `${window.location.origin}/visitas/${visita.id}`;
        const qrCodeDataURL = await QRCode.toDataURL(urlVisualizacao, {
          width: 50,
          margin: 1,
          color: { dark: '#000000', light: '#FFFFFF' }
        });
        doc.addImage(qrCodeDataURL, 'PNG', 15, rodapeY + 2, 25, 25);
        doc.setFontSize(7);
        doc.text('Escaneie para', 27, rodapeY + 28, { align: 'center' });
        doc.text('ver detalhes', 27, rodapeY + 32, { align: 'center' });
      } catch (qrError) {
        console.error('Erro ao gerar QR code:', qrError);
      }

      const nomeArquivo = `COMPROVANTE_VISITA_${visita.codigo}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nomeArquivo);

      return true;
    } catch (error) {
      console.error('Erro ao gerar comprovante de visita:', error);
      return false;
    }
  }
};
